import React, { useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import {
  OrnamentDivider,
  Fleuron,
} from "@/components/Ornament";
import { ScrollReveal } from "@/components/ScrollReveal";
import { Lemon, LemonBranch, PizzaSlice } from "@/components/ItalianOrnaments";
import { PageHeaderBanner } from "@/components/PageHeaderBanner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, ImageIcon, X } from "lucide-react";

const Menu = () => {
  const { t } = useLanguage();
  const sections = t("menu.sections") || [];
  const [openItem, setOpenItem] = useState(null);
  const currency = t("menu.currency") || "€";

  return (
    <div data-testid="menu-page" className="pb-24 bg-sky-roze paper-grain relative overflow-hidden">
      <PageHeaderBanner
        testid="menu-header-banner"
        imgSrc="/illustrations/menu_table.png"
        eyebrow={
          <>
            {t("menu.eyebrow")}
            <span className="font-serif italic normal-case text-[#1F5C8A] text-base tracking-normal pl-2">
              · La carta
            </span>
          </>
        }
        title={t("menu.title")}
        lead={t("menu.lead")}
      />

      <section className="container-roze relative pt-16 md:pt-24">

        <OrnamentDivider className="mb-16" />

        {/* Sections */}
        <div className="space-y-24 md:space-y-32">
          {sections.map((sec, sIdx) => (
            <ScrollReveal
              key={sec.key}
              data-testid={`menu-section-${sec.key}`}
              className="relative"
            >
              {/* Section header */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 mb-10 md:mb-14 items-end">
                <div className="md:col-span-7">
                  <div className="overline text-[#1F5C8A] mb-4 flex items-center gap-3">
                    <span className="font-serif italic normal-case tracking-normal text-base text-[#1F5C8A]/80">
                      {String(sIdx + 1).padStart(2, "0")}.
                    </span>
                    {sec.subtitle}
                  </div>
                  <h2 className="font-serif text-5xl md:text-6xl font-light italic leading-none tracking-tight">
                    {sec.title}
                  </h2>
                </div>
                <div className="md:col-span-5 flex items-end justify-end gap-4 text-[#E0B23E]">
                  {sec.is_pizza ? (
                    <PizzaSlice className="w-14 md:w-20 opacity-90" />
                  ) : sec.key === "dolci" ? (
                    <Lemon className="w-12 md:w-16 opacity-90" />
                  ) : (
                    <span className="block h-px flex-1 bg-gradient-to-r from-transparent via-gold/50 to-gold/60 max-w-[24rem]" />
                  )}
                </div>
              </div>

              {/* Items as photo-cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {sec.items.map((item, iIdx) => (
                  <button
                    key={iIdx}
                    type="button"
                    data-testid={`menu-item-${sec.key}-${iIdx}`}
                    onClick={() => setOpenItem({ ...item, _section: sec })}
                    className="group bg-white/70 border border-border hover:border-gold/60 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex flex-col text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1F5C8A]/40"
                  >
                    {/* Image area */}
                    <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.name}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-1000"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center text-foreground/30 gap-2">
                          <ImageIcon className="h-7 w-7" strokeWidth={1.2} />
                          <span className="text-[10px] uppercase tracking-[0.22em] font-sans">
                            Photo coming soon
                          </span>
                        </div>
                      )}
                      {sec.is_pizza && (
                        <div className="absolute top-3 right-3 px-2 py-1 bg-white/85 text-[#1F5C8A] text-[9px] uppercase tracking-[0.22em]">
                          Pizza
                        </div>
                      )}
                    </div>

                    {/* Text */}
                    <div className="p-5 md:p-6 flex-1 flex flex-col">
                      <div className="flex items-start justify-between gap-4">
                        <h3 className="font-serif text-xl md:text-2xl font-normal leading-tight text-foreground group-hover:text-[#1F5C8A] transition-colors">
                          {item.name}
                        </h3>
                        {typeof item.price === "number" && (
                          <span
                            data-testid={`menu-item-price-${sec.key}-${iIdx}`}
                            className="shrink-0 font-serif text-lg md:text-xl text-[#1F5C8A] tabular-nums"
                          >
                            {item.price}{t("menu.currency") || "€"}
                          </span>
                        )}
                      </div>
                      {item.desc && (
                        <p className="mt-2 text-sm text-foreground/65 leading-relaxed font-light italic">
                          {item.desc}
                        </p>
                      )}
                      {item.allergens && (
                        <div className="mt-auto pt-4 text-[10px] uppercase tracking-[0.18em] text-foreground/55 font-sans">
                          <span className="text-[#1F5C8A]/70">{t("menu.allergens_label")}:</span>{" "}
                          <span className="text-foreground/70">{item.allergens}</span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </ScrollReveal>
          ))}
        </div>

        {/* Note */}
        <ScrollReveal className="mt-24 md:mt-28 max-w-3xl mx-auto text-center">
          <OrnamentDivider className="mb-10" />
          <p className="text-sm md:text-base text-foreground/60 italic font-light leading-relaxed">
            {t("menu.note")}
          </p>
        </ScrollReveal>

        {/* Allergen legend */}
        <ScrollReveal className="mt-16 md:mt-20" data-testid="menu-allergens-legend">
          <div className="max-w-4xl mx-auto bg-white/55 border border-[#1F5C8A]/15 p-8 md:p-10">
            <div className="overline text-[#1F5C8A] mb-5 flex items-center gap-3">
              <Fleuron className="h-3 w-3 text-gold" />
              {t("menu.allergens_legend_title")}
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1.5 text-[12px] text-foreground/70 font-light tabular-nums">
              {(t("menu.allergens_legend") || []).map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </div>
        </ScrollReveal>

        {/* Link to wine list */}
        <ScrollReveal className="mt-12 md:mt-16 text-center" data-testid="menu-wine-link">
          <Link
            to="/wine-list"
            className="group inline-flex items-center gap-3 px-9 py-4 border border-[#1F5C8A]/55 text-foreground hover:bg-white/60 transition-colors text-[11px] uppercase tracking-[0.24em]"
            data-testid="menu-go-to-wine-list"
          >
            {t("menu.wine_link")}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
          </Link>
        </ScrollReveal>

        {/* CTA */}
        <ScrollReveal className="mt-16 md:mt-20 bg-white/70 border border-gold/40 p-10 md:p-14 paper-grain relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <div className="overline mb-4 flex items-center gap-3">
                <Fleuron className="h-3 w-3 text-gold" />
                {t("home.cta_band_overline")}
              </div>
              <h2 className="font-serif text-3xl md:text-5xl font-light leading-tight tracking-tight italic">
                Buon appetito
              </h2>
              <p className="mt-4 text-base md:text-lg text-foreground/75 leading-relaxed max-w-xl font-light">
                {t("home.cta_band_body")}
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
              <Link
                to="/reserve"
                data-testid="menu-cta-reserve"
                className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-xs uppercase tracking-[0.22em]"
              >
                {t("nav.reserve")}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
              </Link>
              <a
                href="tel:+37126111722"
                data-testid="menu-cta-phone"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-gold/60 hover:bg-white/60 transition-colors text-xs uppercase tracking-[0.22em] text-foreground/85"
              >
                <Phone className="h-3.5 w-3.5" />
                +371 26 111 722
              </a>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* Dish detail modal */}
      <Dialog open={!!openItem} onOpenChange={(o) => !o && setOpenItem(null)}>
        <DialogContent
          data-testid="menu-item-modal"
          className="max-w-[94vw] sm:max-w-2xl md:max-w-4xl w-full p-0 overflow-hidden bg-white border border-[#1F5C8A]/15 [&>button]:hidden"
        >
          {openItem && (
            <div className="grid grid-cols-1 md:grid-cols-2 max-h-[92vh] md:max-h-[88vh] overflow-y-auto">
              {/* Image side */}
              <div className="relative h-56 sm:h-72 md:h-auto md:aspect-auto md:min-h-[480px] bg-[#A6CEE3] shrink-0">
                {openItem.image ? (
                  <img
                    src={openItem.image}
                    alt={openItem.name}
                    className="absolute inset-0 w-full h-full object-contain p-2 md:p-3"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-foreground/30 gap-3">
                    <ImageIcon className="h-12 w-12" strokeWidth={1.1} />
                    <span className="text-[10px] uppercase tracking-[0.22em] font-sans">
                      Photo coming soon
                    </span>
                  </div>
                )}
                <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/85 backdrop-blur-sm text-[#1F5C8A] text-[10px] uppercase tracking-[0.22em] font-sans">
                  {openItem._section?.title}
                </div>
              </div>

              {/* Text side */}
              <div className="p-6 sm:p-8 md:p-12 lg:p-14 flex flex-col paper-grain relative">
                <button
                  type="button"
                  onClick={() => setOpenItem(null)}
                  data-testid="menu-item-modal-close"
                  aria-label="Close"
                  className="absolute top-4 right-4 p-2 text-foreground/50 hover:text-foreground transition-colors"
                >
                  <X className="h-5 w-5" strokeWidth={1.5} />
                </button>

                <div className="overline text-[#1F5C8A] mb-4 flex items-center gap-2">
                  <Fleuron className="h-2.5 w-2.5 text-gold" />
                  {openItem._section?.subtitle}
                </div>

                <DialogTitle asChild>
                  <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light leading-[1.05] tracking-tight pr-8">
                    {openItem.name}
                  </h2>
                </DialogTitle>

                {typeof openItem.price === "number" && (
                  <div className="mt-4 md:mt-5 flex items-baseline gap-3">
                    <span className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#1F5C8A] tabular-nums">
                      {openItem.price}
                      {currency}
                    </span>
                  </div>
                )}

                {openItem.desc && (
                  <DialogDescription asChild>
                    <p className="mt-5 md:mt-6 text-sm sm:text-base md:text-lg text-foreground/75 leading-relaxed font-light italic">
                      {openItem.desc}
                    </p>
                  </DialogDescription>
                )}

                {openItem.allergens && (
                  <div className="mt-6 md:mt-8 pt-5 md:pt-6 border-t border-[#1F5C8A]/15">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-foreground/55 font-sans mb-2">
                      {t("menu.allergens_label")}
                    </div>
                    <div className="text-sm text-foreground/75 font-light tabular-nums">
                      {openItem.allergens}
                    </div>
                  </div>
                )}

                <div className="mt-8 md:mt-auto pt-6 md:pt-10">
                  <Link
                    to="/reserve"
                    onClick={() => setOpenItem(null)}
                    data-testid="menu-item-modal-reserve"
                    className="group inline-flex items-center gap-3 px-7 py-3.5 bg-[#1F5C8A] text-white hover:opacity-90 transition-opacity text-[11px] uppercase tracking-[0.24em]"
                  >
                    {t("nav.reserve")}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Menu;
